import { useEffect, useMemo, useRef, useState } from 'react'
import { dirVectors, bearingHexagon, hexagonCrossings, layeredSteps, roundedOutline, roundedPolygon, sonarWedge } from './sonar'

// ─── Hex board — intel peek: bearing hexagons ─────────────────────────────────
// While the eye is held, each live bearing ("A:5") draws:
//   - one hexagon outline in its structure's hue, through the centres of
//     every hex exactly that far from the stamp (no per-hex rings, no pulse);
//     where outlines share a stretch of line they lie side by side, each its
//     own colour, rather than on top of each other (layeredSteps). Its
//     corners are rounded, and it sits dim until the sonar lights it: a
//     brighter copy shows only inside a glow wedge that turns with the arm
//     and fades behind it, so the line seems lit as the arm sweeps past.
//     Over a cratered hex both line and glow run at OVER_CRATER strength;
//   - inside it, an extremely faint darkening: every hex nearer the stamp
//     than the line is where the structure is NOT (the edge runs halfway
//     between the ring and the hexes just inside it). A Hive's insides are
//     merged before the opacity applies, so overlaps don't darken further;
//   - a sonar arm at half strength, sweeping round and round from the stamp
//     out to that line — masked off the stamp's own (black) hex, so it
//     starts at that hex's edge;
//   - a dot wherever two of the SAME structure's hexagons cross (its hue),
//     plus a small ring round the dot where three or more of them pass
//     through the same point. Different structures' hexagons crossing say
//     nothing, so they get no dot. Each dot sits in a barely-there halo one
//     hex wide (a 3-hex structure is often beside a crossing, not on it),
//     and pings — a quick flare — as one of its structure's arms sweeps past.
// One structure's arms all turn together; structures turn on their own beats.
// Tapping a stamp isolates its structure (the rest dim) until it's tapped
// again or the eye is let go.
// Your own Hive (what the enemy knows about you) is drawn flatter, so it
// can't be mistaken for your intel on them: dashed outlines, no arms, no
// glow, no pings.
// Each Hive draws its own, clipped to its land.

export const SONAR_PERIOD_MS = 3200
const LINE_W = 1.5
const CORNER_R = 4
const OUTLINE_DIM = 0.35        // the outline between sweeps
const INSIDE_SHADE = 0.08       // the "not here" darkening inside each hexagon
const SONAR_OPACITY = 0.5
// The arm and its fading trail: [degrees behind the arm, share of SONAR_OPACITY].
const SONAR_TRAIL = [[0, 1], [-7, 0.4], [-14, 0.15]]
const DOT_R = 2.2
const DOT_RING_R = 4.6
const HALO_OPACITY = 0.07       // the "around here" halo under each dot
const PING_DEG = 35             // a dot flares for this much of the arm's turn after it passes
const DIMMED = 0.12             // other structures while one is isolated
const OVER_CRATER = 0.4         // outline and glow strength over a cratered hex
const SELF_DASH = '3 3'
const SELF_OPACITY = 0.5

// The arm's turn. One clock drives the arm AND its glow wedge (the wedge
// lives in a mask, where CSS transform origins can't be trusted), setting
// the same rotate() on both every frame, so the bright stretch always sits
// at the arm's end. A structure's arms share one phase (its beat); beats are
// spread round the dial so structures don't all sweep together.
const phaseOf = (beat) => (beat * 0.37) % 1
const angleAt = (now, beat) => (((now / SONAR_PERIOD_MS) + phaseOf(beat)) % 1) * 360
const now = () => (typeof performance !== 'undefined' ? performance.now() : Date.now())
const sweep = (h) => ({
    'data-sonar': h.beat, 'data-cx': h.cx, 'data-cy': h.cy,
    transform: `rotate(${angleAt(now(), h.beat).toFixed(2)} ${h.cx} ${h.cy})`,
})
// Where a point sits on an arm's dial about (cx, cy): degrees clockwise from
// straight up, the arm's own reckoning.
const dialAngle = (cx, cy, x, y) => ((Math.atan2(x - cx, -(y - cy)) * 180 / Math.PI) + 360) % 360
// How bright a dot flares at arm angle `arm`: full as the arm reaches it,
// fading over PING_DEG of turn behind.
export const pingAt = (arm, dot) => {
    const behind = (arm - dot + 360) % 360
    return behind < PING_DEG ? 1 - behind / PING_DEG : 0
}

const points = (pts) => pts.map(p => `${p.x.toFixed(2)},${p.y.toFixed(2)}`).join(' ')

export default function PeekSonar({ intel, geo, sides }) {
    const drawn = useMemo(() => {
        if (!intel) return null
        const out = {}
        for (const side of sides) {
            const bearings = intel[side]?.bearings ?? []
            if (!bearings.length) continue
            const cells = geo.cells[side]
            const dirs = dirVectors(cells)
            const step = Math.hypot(dirs[0].x, dirs[0].y)
            const colors = [...new Set(bearings.map(b => b.color))]
            const hexes = bearings.map(b => ({ ...b, cx: cells[b.s].cx, cy: cells[b.s].cy,
                beat: colors.indexOf(b.color),
                pts: bearingHexagon(cells[b.s].cx, cells[b.s].cy, b.d, dirs),
                reach: b.d * step }))
            const crossings = colors.flatMap(color => {
                const group = hexes.filter(h => h.color === color)
                return hexagonCrossings(group.map(h => h.pts)).map(p => ({
                    ...p, color, beat: group[0].beat,
                    // Where the dot sits on each of its arms' dials.
                    dials: p.members.map(m => dialAngle(group[m].cx, group[m].cy, p.x, p.y)),
                }))
            })
            const steps = layeredSteps(hexes.map(h => h.pts), hexes.map(h => h.d), LINE_W, hexes.map(h => ({ x: h.cx, y: h.cy })))
            hexes.forEach((h, i) => {
                h.path = roundedOutline(steps[i], CORNER_R)
                h.wedge = sonarWedge(h.cx, h.cy, h.reach * 1.3)
                h.inside = h.d > 1 ? roundedPolygon(bearingHexagon(h.cx, h.cy, h.d - 0.5, dirs), CORNER_R) : null
            })
            out[side] = { hexes, crossings, step, cratered: intel[side]?.cratered ?? [] }
        }
        return out
    }, [intel, geo, sides])

    // The isolated structure (its colour), if a stamp has been tapped.
    const [focus, setFocus] = useState(null)
    useEffect(() => { if (!drawn) setFocus(null) }, [drawn])

    const rootRef = useRef(null)
    useEffect(() => {
        if (!drawn || typeof requestAnimationFrame === 'undefined') return undefined
        let frame
        const tick = () => {
            const t = now()
            const root = rootRef.current
            root?.querySelectorAll('[data-sonar]').forEach(el => {
                const beat = Number(el.dataset.sonar)
                el.setAttribute('transform', `rotate(${angleAt(t, beat).toFixed(2)} ${el.dataset.cx} ${el.dataset.cy})`)
            })
            root?.querySelectorAll('[data-ping]').forEach(el => {
                const arm = angleAt(t, Number(el.dataset.beat))
                const flare = Math.max(0, ...el.dataset.ping.split(',').map(d => pingAt(arm, Number(d))))
                el.setAttribute('opacity', flare.toFixed(3))
                el.setAttribute('r', (DOT_R + 4 * flare).toFixed(2))
            })
            frame = requestAnimationFrame(tick)
        }
        frame = requestAnimationFrame(tick)
        return () => cancelAnimationFrame(frame)
    }, [drawn])

    if (!drawn) return null
    return (
        <g ref={rootRef} style={{ pointerEvents: 'none' }}>
            <defs>
                {sides.filter(side => drawn[side]).map(side => (
                    <clipPath key={side} id={`hexb-peek-land-${side}`}>
                        {geo.cells[side].map(c => <polygon key={c.s} points={c.pts} />)}
                    </clipPath>
                ))}
                {/* Quiets the hexagon lines over cratered hexes. */}
                {sides.filter(side => drawn[side]).map(side => {
                    const grey = Math.round(255 * OVER_CRATER)
                    return (
                        <mask key={`crater-${side}`} id={`hexb-peek-crater-${side}`} maskUnits="userSpaceOnUse"
                            x="-9999" y="-9999" width="19998" height="19998">
                            <rect x="-9999" y="-9999" width="19998" height="19998" fill="#fff" />
                            {drawn[side].cratered.map(s => (
                                <polygon key={s} points={geo.cells[side][s].pts} fill={`rgb(${grey}, ${grey}, ${grey})`} />
                            ))}
                        </mask>
                    )
                })}
                {sides.filter(side => side !== 'self').flatMap(side => (drawn[side]?.hexes ?? []).map((h, i) => (
                    <mask key={`glow-${side}-${i}`} id={`hexb-peek-glow-${side}-${i}`} maskUnits="userSpaceOnUse"
                        x="-9999" y="-9999" width="19998" height="19998">
                        <g {...sweep(h)}>
                            {h.wedge.map((w, k) => <polygon key={k} points={w.points} fill="#fff" fillOpacity={w.opacity} />)}
                        </g>
                    </mask>
                )))}
                {sides.flatMap(side => (drawn[side]?.hexes ?? []).map((h, i) => (
                    <mask key={`${side}-${i}`} id={`hexb-peek-hex-${side}-${i}`} maskUnits="userSpaceOnUse"
                        x="-9999" y="-9999" width="19998" height="19998">
                        <polygon points={points(h.pts)} fill="#fff" />
                        <polygon data-peek-sonar-hole={h.s} points={geo.cells[side][h.s].pts} fill="#000" />
                    </mask>
                )))}
            </defs>
            {sides.filter(side => drawn[side]).map(side => (
                <g key={side} data-peek-side={side} clipPath={`url(#hexb-peek-land-${side})`}>
                    <g data-peek-inside="true" opacity={INSIDE_SHADE}>
                        {drawn[side].hexes.filter(h => h.inside).map((h, i) => <path key={i} d={h.inside} fill="#000" />)}
                    </g>
                    {drawn[side].hexes.map((h, i) => {
                        const flat = side === 'self'
                        return (
                            <g key={`hex-${i}`} data-peek-bearing={`${h.letter ?? ''}:${h.d}`} data-peek-stamp={h.s}
                                opacity={focus && h.color !== focus ? DIMMED : 1}>
                                <g data-peek-quiet="true" mask={`url(#hexb-peek-crater-${side})`}>
                                    <path className="hexb__peek-hex" d={h.path} fill="none" stroke={h.color} strokeWidth={LINE_W}
                                        strokeOpacity={flat ? SELF_OPACITY : OUTLINE_DIM} strokeDasharray={flat ? SELF_DASH : undefined}
                                        strokeLinejoin="round" />
                                    {!flat && (
                                        <g data-peek-glow="true" mask={`url(#hexb-peek-glow-${side}-${i})`}>
                                            <path d={h.path} fill="none" stroke={h.color} strokeWidth={LINE_W * 3} strokeOpacity="0.25" strokeLinejoin="round" />
                                            <path d={h.path} fill="none" stroke={h.color} strokeWidth={LINE_W} strokeLinejoin="round" />
                                        </g>
                                    )}
                                </g>
                                {!flat && (
                                    <g mask={`url(#hexb-peek-hex-${side}-${i})`}>
                                        <g className="hexb__peek-sonar" {...sweep(h)}>
                                            {SONAR_TRAIL.map(([deg, share]) => (
                                                <line key={deg} x1={h.cx} y1={h.cy} x2={h.cx} y2={h.cy - h.reach * 1.2}
                                                    transform={`rotate(${deg} ${h.cx} ${h.cy})`}
                                                    stroke={h.color} strokeOpacity={SONAR_OPACITY * share} strokeWidth={LINE_W} strokeLinecap="butt" />
                                            ))}
                                        </g>
                                    </g>
                                )}
                            </g>
                        )
                    })}
                    {drawn[side].crossings.map((p, k) => (
                        <g key={`x-${k}`} className="hexb__peek-cross" data-peek-crossing={p.members.length}
                            opacity={focus && p.color !== focus ? 0 : 1}>
                            <circle data-peek-halo="true" cx={p.x} cy={p.y} r={drawn[side].step} fill={p.color} fillOpacity={HALO_OPACITY} />
                            {side !== 'self' && (
                                <circle data-ping={p.dials.map(d => d.toFixed(1)).join(',')} data-beat={p.beat}
                                    cx={p.x} cy={p.y} r={DOT_R} fill={p.color} opacity="0" />
                            )}
                            <circle cx={p.x} cy={p.y} r={DOT_R} fill={p.color} stroke="rgba(0, 0, 0, 0.6)" strokeWidth="0.6" />
                            {p.members.length > 2 && (
                                <circle data-peek-cross-ring="true" cx={p.x} cy={p.y} r={DOT_RING_R} fill="none" stroke={p.color} strokeWidth="1" />
                            )}
                        </g>
                    ))}
                    {/* Tap a stamp to isolate its structure; tap it again to let go. */}
                    {drawn[side].hexes.map((h, i) => (
                        <polygon key={`tap-${i}`} data-peek-tap={h.s} points={geo.cells[side][h.s].pts}
                            fill="transparent" style={{ pointerEvents: 'all', cursor: 'pointer' }}
                            onPointerDown={(e) => { e.stopPropagation(); setFocus(f => (f === h.color ? null : h.color)) }} />
                    ))}
                </g>
            ))}
        </g>
    )
}
